/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: InboundRoutesFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing Inbound Routes.
    Manages inbound routing configuration with General and Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Two sections: General and Settings

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Settings, Globe, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { ModuleFormHeader } from '../../components/layouts';
import RecordingSelect from '../../components/ui/RecordingSelect';
import DialProfileSelect from '../../components/ui/DialProfileSelect';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { FormBox } from '../../components/ui/FormBox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select-shadcn';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import { CallerIdInput } from '../../components/ui/CallerIdInput';
import { MultiSelectButton } from '../../components/ui/MultiSelectButton';
import { useSidebarState } from '../../hooks/useSidebarState';
import {
  deleteInboundRouteById,
  navigateToInboundRouteDuplicate,
} from './inboundRoutesActions';
import {
  InboundRoute,
  InboundRouteSettings,
  InboundRouteFormState
} from './types';
import {
  fetchInboundRoute,
  createInboundRoute,
  updateInboundRoute,
  fetchInboundRoutes
} from './api/inboundRouteApi';
import { InboundRouteSettingsSection } from './components/InboundRouteSettingsSection';

/**
 * Raw API response shape for an Inbound Route – properties are in snake_case
 * as delivered by the backend before being mapped to the form state.
 */
interface RawApiInboundRoute {
  name?: string;
  numberPattern?: string;
  number_pattern?: string;
  callerIdFilter?: string;
  caller_id_filter?: string;
  destinationModule?: string;
  destination_module?: string;
  destinationData?: string;
  destination_data?: string;
  priority?: number;
  enabled?: boolean;
  dialProfileId?: number | null;
  dial_profile_id?: number | null;
  settings?: {
    time_condition_id?: number | null;
    timeConditionId?: number | null;
    number_manipulation?: { strip?: number; add?: string };
    failover_module?: string;
    failover_value?: string;
    record_call?: boolean;
    announce_path?: string;
    caller_id_name_rewrite?: string;
    caller_id_number_rewrite?: string;
    block_anonymous?: boolean;
    country_filter?: string | string[];
    allowed_gateways?: string | string[];
    external_api?: string;
    api_fail_action?: string;
    max_concurrent?: number | null;
    limit_action?: string;
    log_calls?: boolean;
    variables?: Record<string, unknown>;
  };
}

const DEFAULT_SETTINGS: InboundRouteSettings = {
  failoverModule: '',
  failoverValue: '',
  recordCall: false,
  announcePath: '',
  numberManipulationStrip: 0,
  numberManipulationAdd: '',
  callerIdNameRewrite: '',
  callerIdNumberRewrite: '',
  blockAnonymous: false,
  countryFilter: [],
  allowedGateways: [],
  externalApi: '',
  apiFailAction: 'continue',
  maxConcurrent: null,
  limitAction: 'busy',
  logCalls: true,
  variables: {},
};

const createDefaultFormState = (): InboundRouteFormState => ({
  name: '',
  numberPattern: '',
  callerIdFilter: '',
  timeConditionId: null,
  destinationModule: '',
  destinationData: '',
  priority: 100,
  enabled: true,
  dialProfileId: null,
  settings: { ...DEFAULT_SETTINGS },
});

const cloneFormState = (state: InboundRouteFormState): InboundRouteFormState => ({
  ...state,
  settings: { ...state.settings, countryFilter: [...state.settings.countryFilter], allowedGateways: [...state.settings.allowedGateways], variables: { ...state.settings.variables } },
});

const serialiseFormState = (state: InboundRouteFormState): string => {
  const sortedSettings = {
    ...state.settings,
    countryFilter: [...state.settings.countryFilter].sort(),
    allowedGateways: [...state.settings.allowedGateways].sort(),
  };

  return JSON.stringify({
    ...state,
    name: state.name.trim(),
    numberPattern: state.numberPattern.trim(),
    callerIdFilter: state.callerIdFilter.trim(),
    destinationModule: state.destinationModule.trim(),
    destinationData: state.destinationData.trim(),
    settings: sortedSettings,
  });
};

const API_FAIL_ACTION_OPTIONS: Array<InboundRouteSettings['apiFailAction']> = [
  'continue',
  'reject',
  'voicemail',
];

const LIMIT_ACTION_OPTIONS: Array<InboundRouteSettings['limitAction']> = [
  'busy',
  'reject',
  'voicemail',
];



export default function InboundRoutesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id && id !== 'new');
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const lastSaveTimestamp = useRef<number>(0);

  const [formState, setFormState] = useState<InboundRouteFormState>(() =>
    createDefaultFormState(),
  );
  const [originalState, setOriginalState] = useState<InboundRouteFormState>(() =>
    createDefaultFormState(),
  );

  const [availableGateways, setAvailableGateways] = useState<
    Array<{ id: string; name: string; description: string }>
  >([]);
  const [availableTimeConditions, setAvailableTimeConditions] = useState
    <Array<{ id: number; name: string; description?: string }>>(
    [],
  );
  const [showAllowedGatewaysModal, setShowAllowedGatewaysModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Quick List state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; uuid?: string; name: string }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);

  // Refs to prevent infinite loops
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined); // Initialize as undefined to detect first mount
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const quickListLoadingRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Refs for stable function references
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const defaults = createDefaultFormState();
      setFormState(defaults);
      setOriginalState(cloneFormState(defaults));
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const hasChanges = useMemo(() => {
    return serialiseFormState(formState) !== serialiseFormState(originalState);
  }, [formState, originalState]);

  const loadGateways = useCallback(async () => {
    try {
      const response = await fetch(
        buildApiUrl('/api/gateways?limit=1000'),
        { headers: getApiHeaders() },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        const gateways = result.data.map((gw: { name: string; description?: string }) => ({
          id: gw.name,
          name: gw.name,
          description: gw.description || '',
        }));
        setAvailableGateways(gateways);
      }
    } catch (error) {
      showErrorRef.current(
        error instanceof Error && error.message.startsWith('HTTP')
          ? `${tRef.current('inboundRoutes:messages.gatewayLoadError')} (${error.message})`
          : tRef.current('inboundRoutes:messages.gatewayLoadError'),
      );
    }
  }, []);

  const loadTimeConditions = useCallback(async () => {
    try {
      const response = await fetch(
        buildApiUrl('/api/telephony/time-conditions?limit=1000'),
        { headers: getApiHeaders() },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setAvailableTimeConditions(result.data);
      }
    } catch (error) {
      showErrorRef.current(
        error instanceof Error && error.message.startsWith('HTTP')
          ? `${tRef.current('inboundRoutes:messages.timeConditionLoadError')} (${error.message})`
          : tRef.current('inboundRoutes:messages.timeConditionLoadError'),
      );
    }
  }, []);

  const applyInboundRouteData = useCallback(
    (route: RawApiInboundRoute, isDuplicate = false) => {
      const settings = route.settings || {};
      const timeConditionId =
        settings.time_condition_id ?? settings.timeConditionId ?? null;
      const numberManipulation = settings.number_manipulation || {};

      const nextState: InboundRouteFormState = {
        name: isDuplicate ? `${route.name} ${tRef.current('common.copySuffix', 'Copy')}` : route.name || '',
        numberPattern: route.numberPattern || route.number_pattern || '',
        callerIdFilter: route.callerIdFilter || route.caller_id_filter || '',
        timeConditionId: timeConditionId,
        destinationModule: timeConditionId ? '' : route.destinationModule || route.destination_module || '',
        destinationData: timeConditionId ? '' : route.destinationData || route.destination_data || '',
        priority: route.priority ?? 100,
        enabled: route.enabled ?? true,
        dialProfileId: route.dialProfileId ?? null,
        settings: {
          ...DEFAULT_SETTINGS,
          failoverModule: settings.failover_module || '',
          failoverValue: settings.failover_value || '',
          recordCall: settings.record_call ?? false,
          announcePath: settings.announce_path || '',
          numberManipulationStrip: numberManipulation.strip ?? 0,
          numberManipulationAdd: numberManipulation.add || '',
          callerIdNameRewrite: settings.caller_id_name_rewrite || '',
          callerIdNumberRewrite: settings.caller_id_number_rewrite || '',
          blockAnonymous: settings.block_anonymous ?? false,
          countryFilter: Array.isArray(settings.country_filter)
            ? settings.country_filter
            : settings.country_filter
              ? [settings.country_filter]
              : [],
          allowedGateways: Array.isArray(settings.allowed_gateways)
            ? settings.allowed_gateways
            : settings.allowed_gateways
              ? [settings.allowed_gateways]
              : [],
          externalApi: settings.external_api || '',
          apiFailAction: API_FAIL_ACTION_OPTIONS.includes(
            settings.api_fail_action as InboundRouteSettings['apiFailAction'],
          )
            ? (settings.api_fail_action as InboundRouteSettings['apiFailAction'])
            : 'continue',
          maxConcurrent:
            settings.max_concurrent === null || settings.max_concurrent === undefined
              ? null
              : Number(settings.max_concurrent),
          limitAction: LIMIT_ACTION_OPTIONS.includes(settings.limit_action as InboundRouteSettings['limitAction'])
            ? (settings.limit_action as InboundRouteSettings['limitAction'])
            : 'busy',
          logCalls: settings.log_calls ?? true,
          variables: settings.variables || {},
        },
      };

      // Use setTimeout to batch state updates and avoid triggering useEffect during render
      setTimeout(() => {
        setFormState(nextState);
        setOriginalState(cloneFormState(nextState));
      }, 0);
    },
    [],
  );

  // Ref for applyInboundRouteData to prevent loadInboundRoute from recreating
  const applyInboundRouteDataRef = useRef(applyInboundRouteData);
  useEffect(() => {
    applyInboundRouteDataRef.current = applyInboundRouteData;
  }, [applyInboundRouteData]);

  // Initialize loadInboundRoute function using useCallback and ref
  const loadInboundRouteFn = useCallback(
    async (routeId: string, duplicate = false) => {
      // Prevent loading if already loading or already loaded this ID
      if (isLoadingRef.current || lastLoadedIdRef.current === routeId) {
        return;
      }

      if (!routeId || routeId === 'new') {
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      setIsLoading(true);
      isLoadingRef.current = true;
      lastLoadedIdRef.current = routeId;

      try {
        const route = await fetchInboundRoute(routeId);
        applyInboundRouteDataRef.current(route, duplicate);
      } catch (error) {
        lastLoadedIdRef.current = null; // Reset on error to allow retry
        showErrorRef.current(
          error instanceof Error && error.message.startsWith('HTTP')
            ? `${tRef.current('inboundRoutes:messages.loadError')} (${error.message})`
            : tRef.current('inboundRoutes:messages.loadError'),
        );
        if (!duplicate) {
          navigateRef.current('/pbx/calls-routing/inbound');
        }
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [],
  );

  const loadInboundRouteRef = useRef(loadInboundRouteFn);
  useEffect(() => {
    loadInboundRouteRef.current = loadInboundRouteFn;
  }, [loadInboundRouteFn]);

  useEffect(() => {
    // Only load initial data once when component mounts
    if (!hasLoadedInitialDataRef.current) {
      hasLoadedInitialDataRef.current = true;
      loadGateways();
      loadTimeConditions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  useEffect(() => {
    // Determine which ID to use (duplicateId takes precedence)
    const loadId = duplicateId || id;

    // Only proceed if loadId actually changed
    const idChanged = currentIdRef.current !== loadId;

    if (!idChanged) {
      return; // No change, skip
    }

    currentIdRef.current = loadId;

    // Load inbound route if editing or duplicating
    if (duplicateId && lastLoadedIdRef.current !== duplicateId && !isLoadingRef.current) {
      // Use setTimeout to avoid calling setState during render
      setTimeout(() => {
        loadInboundRouteRef.current(duplicateId, true);
      }, 0);
    } else if (isEditMode && id && id !== 'new' && lastLoadedIdRef.current !== id && !isLoadingRef.current) {
      // Use setTimeout to avoid calling setState during render
      setTimeout(() => {
        loadInboundRouteRef.current(id, false);
      }, 0);
    } else if (!isEditMode && !duplicateId) {
      // Reset loadedIdRef when creating new
      lastLoadedIdRef.current = null;
      if (!hasInitializedRef.current) {
        // Use setTimeout to avoid calling setState during render
        setTimeout(() => {
          const defaults = createDefaultFormState();
          setFormState(defaults);
          setOriginalState(cloneFormState(defaults));
          hasInitializedRef.current = true;
          setIsLoading(false);
          isLoadingRef.current = false;
        }, 0);
      }
    }
  }, [id, duplicateId, isEditMode]);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  const handleFieldChange = useCallback(
    <K extends keyof InboundRouteFormState>(field: K, value: InboundRouteFormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    },
    [errors],
  );

  const handleSettingChange = useCallback(
    <K extends keyof InboundRouteSettings>(key: K, value: InboundRouteSettings[K]) => {
      setFormState((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [key]: value,
        },
      }));
    },
    [],
  );

  const validateForm = useCallback(() => {
    const validationErrors: Record<string, string> = {};

    if (!formState.name.trim()) {
      validationErrors.name = t('inboundRoutes:validation.nameRequired');
    }

    if (!formState.numberPattern.trim()) {
      validationErrors.numberPattern = t(
        'inboundRoutes:validation.numberPatternRequired',
      );
    }

    if (!formState.timeConditionId) {
      if (!formState.destinationModule.trim()) {
        validationErrors.destinationModule = t(
          'inboundRoutes:validation.destinationModuleRequired',
        );
      }
      if (!formState.destinationData.trim()) {
        validationErrors.destinationData = t(
          'inboundRoutes:validation.destinationDataRequired',
        );
      }
    }

    if (formState.priority < 0) {
      validationErrors.priority = t('inboundRoutes:validation.priorityInvalid');
    }

    if (
      formState.settings.maxConcurrent !== null &&
      (formState.settings.maxConcurrent < 1 ||
        formState.settings.maxConcurrent > 100)
    ) {
      validationErrors.maxConcurrent = t(
        'inboundRoutes:validation.maxConcurrentInvalid',
      );
    }

    setErrors(validationErrors);
    return { isValid: Object.keys(validationErrors).length === 0, errors: validationErrors };
  }, [formState, t]);

  const buildPayload = useCallback(() => {
    const {
      name,
      numberPattern,
      callerIdFilter,
      destinationModule,
      destinationData,
      timeConditionId,
      priority,
      enabled,
      settings,
    } = formState;

    const numberManipulation =
      settings.numberManipulationStrip > 0 || settings.numberManipulationAdd
        ? {
          strip: settings.numberManipulationStrip,
          add: settings.numberManipulationAdd || '',
        }
        : {};

    const normalizedSettings = {
      failover_module: settings.failoverModule || null,
      failover_value: settings.failoverValue || null,
      time_condition_id: timeConditionId || null,
      record_call: settings.recordCall,
      announce_path: settings.announcePath || null,
      number_manipulation: numberManipulation,
      caller_id_name_rewrite: settings.callerIdNameRewrite || null,
      caller_id_number_rewrite: settings.callerIdNumberRewrite || null,
      block_anonymous: settings.blockAnonymous,
      country_filter: settings.countryFilter,
      allowed_gateways: settings.allowedGateways,
      external_api: settings.externalApi || null,
      api_fail_action: settings.apiFailAction,
      max_concurrent: settings.maxConcurrent,
      limit_action: settings.limitAction,
      log_calls: settings.logCalls,
      variables: settings.variables || {},
    };

    return {
      name: name.trim(),
      numberPattern: numberPattern.trim(),
      callerIdFilter: callerIdFilter.trim() || null,
      destinationModule: timeConditionId ? null : destinationModule.trim(),
      destinationData: timeConditionId ? null : destinationData.trim(),
      priority,
      enabled,
      dialProfileId: formState.dialProfileId,
      settings: normalizedSettings,
    };
  }, [formState]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<InboundRoute | undefined> => {
      event?.preventDefault();

      const validation = validateForm();
      if (!validation.isValid) {
        const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
        showError(`${t('inboundRoutes:messages.fixValidationErrors')}${errorMsgs ? ': ' + errorMsgs : ''}`);
        return undefined;
      }

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      const payload = buildPayload();

      setIsSaving(true);
      try {
        let result;
        if (isEditMode && id) {
          result = await updateInboundRoute(id, payload);
        } else {
          result = await createInboundRoute(payload);
        }

        const nextState = cloneFormState(formState);
        setOriginalState(nextState);

        // Show success notification after successful save
        showSuccess(
          isEditMode
            ? t('inboundRoutes:messages.saveSuccessUpdate')
            : t('inboundRoutes:messages.saveSuccessCreate'),
        );

        // Return saved data for useSaveWithAction
        return result;
      } catch (error) {
        const msg = error instanceof Error ? error.message : t('inboundRoutes:messages.saveError');
        showError(msg);
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [
      buildPayload,
      formState,
      hasChanges,
      id,
      isEditMode,
      showError,
      showInfo,
      showSuccess,
      t,
      validateForm,
    ],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as InboundRoute;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/calls-routing/inbound',
    newPath: '/pbx/calls-routing/inbound/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Inbound Routes use ID for navigation
      return `/pbx/calls-routing/inbound/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formState
    }),
    storageKey: 'inboundRoutes:saveAndNewData'
  });

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState(cloneFormState(originalState));
      setErrors({});
    } else {
      const defaults = createDefaultFormState();
      setFormState(defaults);
      setOriginalState(cloneFormState(defaults));
      setErrors({});
    }
  }, [isEditMode, originalState]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteInboundRouteById(id);
      showSuccess(t('inboundRoutes:messages.deleteSuccess'));
      navigate('/pbx/calls-routing/inbound');
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('inboundRoutes:messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToInboundRouteDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/calls-routing/inbound/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const result = await fetchInboundRoutes(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc'
        },
        undefined,
        'name'
      );

      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; uuid?: string; name: string }>)
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('inboundRoutes:messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query),
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<ArrowLeft className="h-5 w-5 text-primary" />}
        backButtonText={t('inboundRoutes:actions.back')}
        onBack={() => navigate('/pbx/calls-routing/inbound')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('inboundRoutes:actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('inboundRoutes:actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('inboundRoutes:actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <FormBox
            title={t('inboundRoutes:sections.general')}
            icon={<ArrowLeft className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              <Label htmlFor="name">
                <SimpleTooltip content={t('inboundRoutes:fields.name.tooltip')}>
                  <span>
                    {t('inboundRoutes:fields.name.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="name"
                  value={formState.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder={t('inboundRoutes:fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              <Label htmlFor="numberPattern">
                <SimpleTooltip content={t('inboundRoutes:fields.numberPattern.tooltip')}>
                  <span>
                    {t('inboundRoutes:fields.numberPattern.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="numberPattern"
                  value={formState.numberPattern}
                  onChange={(e) => handleFieldChange('numberPattern', e.target.value)}
                  placeholder={t('inboundRoutes:fields.numberPattern.placeholder')}
                  className={errors.numberPattern ? 'border-red-500' : ''}
                />
                {errors.numberPattern && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.numberPattern}
                  </p>
                )}
              </div>

              <Label htmlFor="callerIdFilter">
                <SimpleTooltip content={t('inboundRoutes:fields.callerIdFilter.tooltip')}>
                  {t('inboundRoutes:fields.callerIdFilter.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="callerIdFilter"
                  value={formState.callerIdFilter}
                  onChange={(e) => handleFieldChange('callerIdFilter', e.target.value)}
                  placeholder={t('inboundRoutes:fields.callerIdFilter.placeholder')}
                />
              </div>

              <Label htmlFor="timeConditionId">
                <SimpleTooltip content={t('inboundRoutes:fields.timeCondition.tooltip')}>
                  {t('inboundRoutes:fields.timeCondition.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Select
                  value={
                    formState.timeConditionId
                      ? formState.timeConditionId.toString()
                      : ''
                  }
                  onValueChange={(value) => {
                    const nextValue = value ? parseInt(value, 10) : null;
                    handleFieldChange('timeConditionId', nextValue);
                    if (nextValue) {
                      handleFieldChange('destinationModule', '');
                      handleFieldChange('destinationData', '');
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('inboundRoutes:fields.timeCondition.placeholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">
                      {t('common.none')}
                    </SelectItem>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    {availableTimeConditions.map((tc: any) => (
                      <SelectItem key={tc.id} value={tc.id.toString()}>
                        {tc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!formState.timeConditionId && (
                <>
                  <Label htmlFor="destinationModule" className="self-start pt-2">
                    <SimpleTooltip content={t('inboundRoutes:fields.destinationModule.tooltip')}>
                      <span>
                        {t('inboundRoutes:fields.destinationModule.label')}
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                    </SimpleTooltip>
                  </Label>
                  <div className="col-span-3">
                    <ModuleDestinationSelect
                      moduleValue={formState.destinationModule}
                      destinationValue={formState.destinationData}
                      onModuleChange={(value) =>
                        handleFieldChange('destinationModule', value || '')
                      }
                      onDestinationChange={(value) =>
                        handleFieldChange('destinationData', value || '')
                      }
                      modulePlaceholder={t('inboundRoutes:fields.destinationModule.placeholder')}
                      destinationPlaceholder={t('inboundRoutes:fields.destinationData.placeholder')}
                    />
                    {(errors.destinationModule || errors.destinationData) && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.destinationModule || errors.destinationData}
                      </p>
                    )}
                  </div>
                </>
              )}

              <Label className="self-start pt-2">
                <SimpleTooltip content={t('inboundRoutes:fields.failoverModule.tooltip')}>
                  {t('inboundRoutes:fields.failoverModule.label')}
                </SimpleTooltip>
              </Label>
              <div className="col-span-3">
                <ModuleDestinationSelect
                  moduleValue={formState.settings.failoverModule}
                  destinationValue={formState.settings.failoverValue}
                  onModuleChange={(value) =>
                    handleSettingChange('failoverModule', value || '')
                  }
                  onDestinationChange={(value) =>
                    handleSettingChange('failoverValue', value || '')
                  }
                  modulePlaceholder={t('inboundRoutes:fields.failoverModule.placeholder')}
                  destinationPlaceholder={t('inboundRoutes:fields.failoverValue.placeholder')}
                />
              </div>

              <Label htmlFor="dialProfileId">
                <SimpleTooltip content={t('inboundRoutes:fields.dialProfile.tooltip', 'Select a Dial Profile to control call routing parameters.')}>
                  {t('inboundRoutes:fields.dialProfile.label', 'Dial Profile')}
                </SimpleTooltip>
              </Label>
              <div>
                <DialProfileSelect
                  value={formState.dialProfileId}
                  onChange={(val) => handleFieldChange('dialProfileId', val)}
                  placeholder={t('inboundRoutes:fields.dialProfile.placeholder', 'Select Dial Profile')}
                  nullLabel={t('inboundRoutes:fields.dialProfile.nullLabel', 'None (no profile)')}
                  allowNull
                />
              </div>

              <Label htmlFor="priority">
                <SimpleTooltip content={t('inboundRoutes:fields.priority.tooltip')}>
                  {t('inboundRoutes:fields.priority.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="priority"
                  type="number"
                  min={0}
                  value={formState.priority}
                  onChange={(e) =>
                    handleFieldChange('priority', parseInt(e.target.value, 10) || 0)
                  }
                  className={errors.priority ? 'border-red-500' : ''}
                />
                {errors.priority && (
                  <p className="text-sm text-red-500 mt-1">{errors.priority}</p>
                )}
              </div>

              <Label>
                <SimpleTooltip content={t('inboundRoutes:fields.enabled.tooltip')}>
                  {t('inboundRoutes:fields.enabled.label')}
                </SimpleTooltip>
              </Label>
              <div>
                <Toggle
                  value={formState.enabled}
                  onChange={(checked) => handleFieldChange('enabled', checked)}
                />
              </div>
            </div>
          </FormBox>

          <InboundRouteSettingsSection
            settings={formState.settings}
            errors={errors}
            availableGateways={availableGateways}
            showAllowedGatewaysModal={showAllowedGatewaysModal}
            setShowAllowedGatewaysModal={setShowAllowedGatewaysModal}
            onSettingChange={handleSettingChange}
            t={t}
          />

        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isLoading={isLoading}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="inboundRoutes:saveActionPreference"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('inboundRoutes:messages.deleteTitle')}
        message={t('inboundRoutes:messages.confirmDeleteGeneric')}
        confirmText={t('inboundRoutes:actions.delete')}
        cancelText={t('inboundRoutes:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />



      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('inboundRoutes:quickList.title', 'Inbound Routes')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/calls-routing/inbound/${item.uuid ?? item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/calls-routing/inbound/${item.uuid ?? item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
          </button>
        )}
        searchPlaceholder={t('inboundRoutes:quickList.searchPlaceholder', 'Search by name')}
        emptyMessage={t('inboundRoutes:quickList.empty', 'No inbound routes found')}
        headerIcon={<Globe className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

